import Box from '@mui/material/Box';
import type { ReactNode } from 'react';
import { useNameValues, usePanelValues } from '../../lib/filters.hooks';
import { FILTER_BAR_ROW_SX } from '../filters/menuStyles';
import { DateRangeControl, MultiScopeSelectControl } from '../filters/ScopeRow';
import { SingleSelectControl } from '../filters/SingleSelectControl';
import type { ScopeState } from '../filters/types';
import { useScopeOptions } from '../filters/useScopeOptions';
import { CREATIVE_TYPE_PANEL_KEYS, creativeTypeOptions } from '../filters/WorkflowsFilterBar';
import type { MediaKpi } from '../media/data/types';

interface ReportsFilterBarProps {
  scope: ScopeState;
  onScopeChange: (scope: ScopeState) => void;
  dateRange: [string, string];
  accountIds: number[];
  kpi: string;
  onKpiChange: (kpi: string) => void;
  kpiOptions: MediaKpi[];
  campaigns: string[];
  onCampaignsChange: (campaigns: string[]) => void;
}

export function ReportsFilterBar({
  scope,
  onScopeChange,
  dateRange,
  accountIds,
  kpi,
  onKpiChange,
  kpiOptions,
  campaigns,
  onCampaignsChange,
}: ReportsFilterBarProps): ReactNode {
  const { brandOptions, platformOptions, accountOptions } = useScopeOptions(scope);
  const { data: creativeTypePanelValues } = usePanelValues(
    CREATIVE_TYPE_PANEL_KEYS,
    undefined,
    dateRange,
    accountIds,
  );
  const { data: campaignNames } = useNameValues('campaignName', dateRange, accountIds);
  const campaignOptions = (campaignNames?.campaignName?.values ?? []).map((name) => ({
    id: name,
    label: name,
  }));

  return (
    <Box sx={FILTER_BAR_ROW_SX}>
      <MultiScopeSelectControl
        label="Brand"
        showSingleOptionName
        placeholderLabel="All brands"
        options={brandOptions}
        selected={scope.brand}
        onChange={(brand) => onScopeChange({ ...scope, brand, platform: [], account: [] })}
      />
      <MultiScopeSelectControl
        label="Platform"
        showSingleOptionName
        placeholderLabel="All platforms"
        options={platformOptions}
        selected={scope.platform}
        onChange={(platform) => onScopeChange({ ...scope, platform, account: [] })}
      />
      <MultiScopeSelectControl
        label="Ad Account"
        showSingleOptionName
        placeholderLabel="All ad accounts"
        options={accountOptions}
        selected={scope.account}
        onChange={(account) => onScopeChange({ ...scope, account })}
      />
      <MultiScopeSelectControl
        label="Creative Type"
        placeholderLabel="All creative types"
        options={creativeTypeOptions(creativeTypePanelValues)}
        selected={scope.creativeType}
        onChange={(creativeType) => onScopeChange({ ...scope, creativeType })}
      />
      <MultiScopeSelectControl
        label="Campaign"
        placeholderLabel="All campaigns"
        options={campaignOptions}
        selected={campaigns}
        onChange={onCampaignsChange}
      />
      <DateRangeControl
        value={scope.range}
        onChange={(range) => onScopeChange({ ...scope, range })}
        resolved={dateRange}
      />
      <SingleSelectControl
        label="KPI"
        options={kpiOptions.map((option) => ({ id: option.id, label: option.label }))}
        value={kpi}
        onChange={onKpiChange}
      />
    </Box>
  );
}
